import { useState, useEffect, memo } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
} from "react-simple-maps";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const FlightMap = memo(({ mapData }) => {
  const [tooltip, setTooltip] = useState(null);

  if (!mapData || (!mapData.airports?.length && !mapData.routes?.length)) {
    return (
      <div className="h-64 flex items-center justify-center text-white/40">
        <p>Log flights to see your travel map</p>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="interactive-map">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 120,
          center: [0, 30],
        }}
        style={{
          width: "100%",
          height: "auto",
        }}
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill="#1A1A1A"
                stroke="#333333"
                strokeWidth={0.5}
                style={{
                  default: { outline: "none" },
                  hover: { outline: "none", fill: "#252525" },
                  pressed: { outline: "none" },
                }}
              />
            ))
          }
        </Geographies>

        {/* Flight Routes */}
        {mapData.routes?.map((route, index) => (
          <Line
            key={`route-${index}`}
            from={route.fromCoords}
            to={route.toCoords}
            stroke="#007AFF"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeOpacity={0.6}
            style={{
              default: { transition: "stroke-opacity 0.3s" },
              hover: { strokeOpacity: 1, strokeWidth: 2 },
            }}
          />
        ))}

        {/* Airport Markers */}
        {mapData.airports?.map((airport) => (
          <Marker
            key={airport.code}
            coordinates={airport.coordinates}
            onMouseEnter={() => setTooltip(airport)}
            onMouseLeave={() => setTooltip(null)}
          >
            <circle
              r={Math.min(4 + airport.flights, 10)}
              fill="#007AFF"
              stroke="#FFFFFF"
              strokeWidth={1.5}
              style={{ cursor: "pointer" }}
            />
            <text
              textAnchor="middle"
              y={-12}
              style={{
                fontFamily: "JetBrains Mono, monospace",
                fontSize: "8px",
                fill: "#FFFFFF",
                fontWeight: 600,
              }}
            >
              {airport.code}
            </text>
          </Marker>
        ))}
      </ComposableMap>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute top-4 right-4 bg-[#111111] border border-white/20 rounded-sm p-3 pointer-events-none"
          data-testid="map-tooltip"
        >
          <p className="font-mono text-[#007AFF] font-bold">{tooltip.code}</p>
          <p className="text-white text-sm">{tooltip.city}</p>
          <p className="text-white/60 text-xs">{tooltip.flights} flight(s)</p>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-[#0A0A0A]/80 border border-white/10 rounded-sm p-2 text-xs">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full bg-[#007AFF]"></div>
          <span className="text-white/60">Airport</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 bg-[#007AFF]"></div>
          <span className="text-white/60">Flight Route</span>
        </div>
      </div>
    </div>
  );
});

FlightMap.displayName = "FlightMap";

export default FlightMap;
